import React from 'react';
import { useSelector } from 'react-redux';
import ArrowForward from '@material-ui/icons/ArrowRightAlt';
import { Button, Modal } from '@solvhealth/jigsaw-v1';
import { setCookie } from '../../../../../../core/util/cookies';
import { CookieNames } from '../../../../../../config/index';
import { analyticsTrackEvent } from '../../../../../../core/analytics';
import {
  Actions,
  Events,
  Sources,
} from '../../../../../Account/Profile/Insurance/Tutorial/analytics';
import {
  INSURANCE_TUTORIAL_ENTRY_POINT_MODAL,
  INSURANCE_TUTORIAL_MODAL,
} from '../../../../../../constants/modalIds';
import {
  getPlanTypeOrEmptyString,
  isWhitelistedPlanType,
  shouldShowSwimlanes,
} from '../../../../../Account/Profile/Insurance/util/insuranceDetails';
import Observer from '../../../../../SolvPatternLibrary/Profilers/Observer';
import useSelectInsuranceProfile from '../../../../hooks/useSelectInsuranceProfile';
import useModal from '../../../../../../hooks/useModal';
import useSelectFetchInsuranceDetails from '../../../../hooks/useSelectFetchInsuranceDetails';

import {
  CopyContainer,
  H2,
  Icon,
  ImgContainer,
  P,
  Root,
} from '~/components/Account/Profile/Insurance/Tutorial/ContentCards/Base';

type Props = {
  userProfileId: string;
  open: boolean;
};

/** TODO:
 * This file is almost a complete duplicate of src/components/AccountGetsMarried/components/Insurance/components/EntryPoint/Card.tsx.
 * Please consider consolidating the two before editing
 */
const InsuranceTutorialModal = (props: Props) => {
  const { userProfileId, open } = props;
  const insuranceProfile = useSelectInsuranceProfile(userProfileId);
  const insuranceDetails = useSelectFetchInsuranceDetails(userProfileId);
  const insuranceDetailsSubmitting = useSelector((state: any) => state.insuranceDetails.submitting);
  const npi = useSelector((state: any) => state.npi);

  const firstName = insuranceProfile && (insuranceProfile.first_name || insuranceProfile.firstName);
  const insuranceCarrier =
    insuranceProfile && (insuranceProfile.insurer_name || insuranceProfile.insurerName);

  const { openModal, closeModal } = useModal();

  const handleCloseModal = () => {
    setCookie(CookieNames.INSURANCE_TUTORIAL);
    closeModal(INSURANCE_TUTORIAL_ENTRY_POINT_MODAL);
  };

  const handleShowModal = () => {
    analyticsTrackEvent(Events.INSURANCE_TUTORIAL, { action: Actions.SHOW_ENTRY_POINT_MODAL });
  };

  const handleClick = () => {
    analyticsTrackEvent(Events.INSURANCE_TUTORIAL, {
      action: Actions.CLICK_VIEW_TUTORIAL,
      source: Sources.TUTORIAL_ENTRY_POINT_MODAL,
    });
    closeModal(INSURANCE_TUTORIAL_ENTRY_POINT_MODAL);
    openModal(INSURANCE_TUTORIAL_MODAL);
  };

  const shouldShow = () => {
    const planType = getPlanTypeOrEmptyString(insuranceDetails);
    return (
      isWhitelistedPlanType(planType) &&
      shouldShowSwimlanes({
        detailsResult: insuranceDetails,
        insuranceProfile,
        insuranceDetailsSubmitting,
        npi,
      })
    );
  };

  if (!shouldShow()) return null;

  return (
    <Modal onClose={handleCloseModal} open={open}>
      <Observer handleSawComponent={handleShowModal}>
        <Root>
          <ImgContainer>
            <Icon src="/images/insurance_tutorial/you_have_insurance.svg" />
          </ImgContainer>

          <CopyContainer>
            <H2>
              Get to know {firstName}'s {insuranceCarrier} plan
            </H2>

            <P>
              Learn how it works, what's covered, and what all of those bizarre insurance terms
              mean.
            </P>
          </CopyContainer>

          <Button mx="auto" onClick={handleClick} variant="contained">
            Let's Go <ArrowForward />
          </Button>
        </Root>
      </Observer>
    </Modal>
  );
};

export default InsuranceTutorialModal;
